'use client'

import { useState, useEffect } from 'react'
import { Clock, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { executionsAPI } from '@/lib/api'
import ApprovalDetailModal, { PendingStep } from '@/components/approvals/ApprovalDetailModal'

export default function PendingApprovals() {
  const [steps, setSteps] = useState<PendingStep[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedStep, setSelectedStep] = useState<PendingStep | null>(null)

  const load = async () => {
    try {
      const res = await executionsAPI.getPendingApprovals()
      if (res.data?.success) setSteps(res.data.data)
    } catch {
      // silently fail in dashboard widget
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleApproved = (stepId: string) => {
    setSteps(prev => prev.filter(s => s.id !== stepId))
    setSelectedStep(null)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
        </div>
        <div className="flex justify-center py-6">
          <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
          <div className="flex items-center gap-3">
            {steps.length > 0 && (
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                {steps.length} pending
              </span>
            )}
            <Link
              href="/approvals"
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {steps.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No pending approvals.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {steps.slice(0, 3).map(step => (
              <ApprovalCard
                key={step.id}
                step={step}
                onReview={() => setSelectedStep(step)}
              />
            ))}
          </div>
        )}
      </div>

      <ApprovalDetailModal
        step={selectedStep}
        onClose={() => setSelectedStep(null)}
        onApproved={handleApproved}
      />
    </>
  )
}

function ApprovalCard({ step, onReview }: { step: PendingStep; onReview: () => void }) {
  const agentName = step.execution.agent.name
  const accountName = step.execution.account.name

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 min-w-0">
          <div className="p-1 rounded-full bg-yellow-100 flex-shrink-0">
            <Clock className="h-4 w-4 text-yellow-600" />
          </div>
          <span className="font-medium text-gray-900 text-sm truncate">{accountName}</span>
        </div>
        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">Step {step.stepNo}</span>
      </div>
      <p className="text-sm text-gray-600 mb-1 truncate">{step.stepName}</p>
      <p className="text-xs text-gray-400 mb-3 truncate">{agentName}</p>
      <button
        onClick={onReview}
        className="w-full bg-blue-600 text-white text-sm py-1.5 px-3 rounded hover:bg-blue-700 transition-colors"
      >
        Review
      </button>
    </div>
  )
}
